import { Link } from 'react-router-dom'
import Section from '@/components/Section'
import PageHero from '@/components/PageHero'
import { usePageTitle } from '@/components/usePageTitle'

export default function Faq() {
  usePageTitle('FAQ', 'Frequently asked questions about registering a color with ColorMark.')
  return (
    <>
      <PageHero image="/images/faq-bg.webp" overlay={0.07} title="Frequently asked questions." as="h2" />

      <Section>
        <div className="prose-cm mx-auto">
          <h3>Why register a color?</h3>
          <p>
            When you register a color, you provide a unique color name. You can select your favorite color and name it
            anything you want, even name it after yourself. Each color registration comes with a custom color website
            and Official Certificate of Registration.
          </p>
          <p>
            ColorMark registrations make a great gift! You can register a color and name it after your family and
            friends. Customized color registrations are unique, delightful, personal, and (of course) colorful gifts
            for any occasion including birthdays, weddings, and holidays. Additionally, you can buy customized
            merchandise including posters, canvas, and framed prints in a variety of designs in your color.
          </p>
          <hr />

          <h3>How many colors are available?</h3>
          <p>
            There is a limited supply of colors available to register. There are 16,777,216 total colors in the
            hexadecimal color space. Of these, 140 are reserved color (e.g. Red, Green, Blue) and 12,300 are premium
            colors. To date, over 1 million colors have been named and are no longer available.
          </p>
          <hr />

          <h3>Do you offer bulk packages or multi-color deals?</h3>
          <p>
            <strong className="font-semibold italic">COMING SOON!</strong> ColorMark will soon offer discounts for
            purchasing a block of colors.
          </p>
          <hr />

          <h3>What merchandise is available?</h3>
          <p>
            Please go to our <Link to="/store">Store</Link> to view ColorMark merchandise including prints, stickers,
            and mugs.
          </p>
          <ul>
            <li>
              <strong className="font-semibold">Posters:</strong> We provide a great selection of museum-quality
              posters made on thick and durable matte paper in a variety of sizes from 8x10 to 24x36. Add a wonderful
              accent to your room and office with these posters that are sure to brighten any environment.
            </li>
            <li>
              <strong className="font-semibold">Stickers:</strong> These stickers are printed on durable, high opacity
              adhesive vinyl which makes them perfect for regular use, as well as for covering other stickers or paint.
              The high-quality vinyl ensures there are no bubbles when applying the stickers.
            </li>
            <li>
              <strong className="font-semibold">Mugs:</strong> Whether you're drinking your morning coffee, evening
              tea, or something in between – this mug's for you! It's sturdy and glossy with a vivid print that'll
              withstand the microwave and dishwasher.
            </li>
          </ul>
          <p>
            <strong className="font-semibold italic">COMING SOON!</strong> ColorMark will soon offer merchandise for
            registered colors that is customized with your color/name.
          </p>
          <hr />

          <h3>How long does registration last?</h3>
          <p>
            Standard registration is for 1 year and may be renewed annually. If registration is not renewed the color
            will expire and become available for the public to rename.
          </p>
          <p>
            Extended registration from ColorMark is now available with options for 2 years, 5 years, and even Lifetime
            registration.
          </p>
          <hr />

          <h3>How much does registration cost?</h3>
          <p>Registration is $19.95 annually with discounts for multi-year registration.</p>
          <ul>
            <li>1 Year Registration: $19.95</li>
            <li>2 Year Registration: $29.95 (Save 25%)</li>
            <li>5 Year Registration: $49.95 (Save 50%)</li>
            <li>Lifetime Registration: $99.95 (Best Value)</li>
          </ul>
          <hr />

          <h3>How are color registrations published?</h3>
          <p>
            All active color registrations can be searched in the online <Link to="/registry">ColorMark Registry</Link>
            . Additionally, the International ColorMark Organization publishes an annual compendium of all purchased
            colors which will be registered with the <a href="https://loc.gov">Library of Congress</a> and the{' '}
            <a href="https://copyright.gov">United States Copyright Office</a>.
          </p>
          <hr />

          <h3>Can I change the name of my color?</h3>
          <p>
            Yes! You can change the name of your color at anytime while your registration is active. Please{' '}
            <Link to="/contact">contact us</Link> to request a registration name change. Please note that all color
            names must abide by our <Link to="/terms-of-service">Terms of Service</Link> and must be unique. Please
            check the <Link to="/registry">ColorMark Registry</Link> to see if your name is still available.
          </p>
          <hr />

          <h3>How do I report a complaint about a registered color?</h3>
          <p>
            Please <Link to="/contact">contact us</Link> to report any color names in the ColorMark registry which
            violate our <Link to="/terms-of-service">Terms of Service</Link> including offensive/vulgar names,
            trademark violations, or intentional misspellings.
          </p>
          <hr />

          <h3>Are you affiliated with any government?</h3>
          <p>
            No. The International ColorMark Organization is a private, non-governmental organization (NGO) dedicated to
            giving people the joy of being able to name and register their own unique color. We are in no way
            affiliated with any government office or agency and ColorMark registration does not provide any
            intellectual property rights.
          </p>
        </div>
      </Section>
    </>
  )
}
